import React from "react";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";

// MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Styled Components
import { StyledContainerBox } from "../../styled/superficies";
import { ModernButton } from "../../styled/button";

// Modal Base
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const MerchantSetupModal = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const history = useHistory();

  const handleSetupNow = () => {
    history.push("/restaurant/new");
    onClose();
  };

  // Concept Cards Data
  const conceptCards = [
    {
      icon: BusinessIcon,
      color: theme.palette.primary.main,
      title: "Base de Tudo",
      description:
        "Todos os pedidos, produtos e entregas estão vinculados a uma loja. É o núcleo da sua operação.",
    },
    {
      icon: AccountTreeIcon,
      color: theme.palette.success.main,
      title: "Multi-Lojas",
      description:
        "Gerencie várias lojas com diferentes cardápios, zonas e configurações em um único painel.",
    },
    {
      icon: RestaurantMenuIcon,
      color: theme.palette.warning.main,
      title: "Cardápio Digital",
      description:
        "Crie produtos, organize categorias e construa seu catálogo digital completo.",
    },
  ];

  // Platform Integration Data
  const platforms = [
    {
      name: "iFood",
      logo: "/images/logo-ifood.png",
      color: "#EA1D2C",
      features: ["Importar Catálogo", "Entrega Fácil"],
      description: "Importe produtos e use motoboys do iFood",
    },
    {
      name: "Uber Direct",
      logo: "/images/uber-logo-small.png",
      color: "#000000",
      features: ["Cotações em Tempo Real", "Rastreamento"],
      description: "Logística sob demanda com rastreamento ao vivo",
    },
  ];

  return (
    <AnimatedModal isOpen={isOpen} width="90%" maxWidth="900px" zIndex={9999}>
      <ModalHeader title="Configure Sua Primeira Loja" borderBottom={true} />

      <ModalContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Hero Section */}
          <Box
            sx={{
              position: "relative",
              p: { xs: 3, md: 4 },
              mb: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg,
                ${alpha(theme.palette.primary.main, 0.05)} 0%,
                ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Circle */}
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: alpha(theme.palette.primary.main, 0.03),
                zIndex: 0,
              }}
            />

            {/* Animated Icon */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{ position: "relative", zIndex: 1 }}
            >
              <StorefrontIcon
                sx={{
                  fontSize: { xs: 60, md: 80 },
                  color: theme.palette.primary.main,
                  mb: 2,
                  filter: `drop-shadow(0 4px 12px ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )})`,
                }}
              />
            </motion.div>

            {/* Hero Text */}
            <Typography
              variant="h4"
              fontWeight={700}
              gutterBottom
              sx={{
                fontSize: { xs: "1.5rem", md: "2.125rem" },
                position: "relative",
                zIndex: 1,
              }}
            >
              Sua Loja é o Coração de Cada Pedido
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 600,
                mx: "auto",
                position: "relative",
                zIndex: 1,
              }}
            >
              Cada produto, entrega e integração de plataforma começa com uma
              loja. Configure sua primeira loja para desbloquear todo o poder do
              Slapie.
            </Typography>
          </Box>

          {/* Why Merchants Matter - 3 Column Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {conceptCards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <StyledContainerBox
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      p: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme.shadows[4],
                        borderColor: alpha(card.color, 0.3),
                      },
                    }}
                  >
                    {/* Circular Icon Container */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        mx: "auto",
                        mb: 2,
                        borderRadius: "50%",
                        background: alpha(card.color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <card.icon sx={{ fontSize: 32, color: card.color }} />
                    </Box>

                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {card.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </StyledContainerBox>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Platform Integration Showcase */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              fontWeight={600}
              textAlign="center"
              gutterBottom
              sx={{ mb: 3 }}
            >
              Desbloqueie Estas Integrações
            </Typography>

            <Grid container spacing={2}>
              {platforms.map((platform, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border: `2px solid ${alpha(platform.color, 0.2)}`,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          boxShadow: theme.shadows[8],
                          borderColor: platform.color,
                          transform: "translateY(-4px)",
                        },
                      }}
                    >
                      {/* Platform Logo */}
                      <Box
                        sx={{
                          height: 60,
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={platform.logo}
                          alt={platform.name}
                          style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      {/* Platform Name */}
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {platform.name}
                      </Typography>

                      {/* Feature Chips */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                        sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}
                      >
                        {platform.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: alpha(platform.color, 0.1),
                              color: platform.color,
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                          />
                        ))}
                      </Stack>

                      {/* Description */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: "auto" }}
                      >
                        {platform.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </ModalContent>

      <ModalFooter borderTop={true}>
        <ModernButton
          onClick={handleSetupNow}
          fullWidth
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 2,
            fontSize: "1.1rem",
          }}
        >
          Cadastrar Minha Primeira Loja
        </ModernButton>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default MerchantSetupModal;
